import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import Percent from "_shared/percentages/percent";
import _ from "lodash";
import Session from "users/__session/session";
import User from "users/user";
import {
  FeeScheduleCategoryMaskedCurrency,
  Masked,
  MaskedContracts,
  MaskedDiscount,
  MaskedFeeSchedule,
  MaskedTeam,
} from "work/_shared/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { ProposalAction, ProposalStatus } from "work/proposals/__constants/constants";
import ProposalSchema, {
  BaseProposalParams,
  ProposalJSON,
  ProposalParams,
} from "work/proposals/_schemas/proposal-schema";
import ProposalField, { ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import WorkAgreementSchema from "work/work-agreements/_schemas/work-agreement-codec";
import { WorkAgreementParams } from "work/work-agreements/_schemas/work-agreement-schema";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import WorkAgreement from "work/work-agreements/work-agreement";
import { Audience } from "./comments/threads/topics/topic-context";
import { FeeScheduleRedline } from "./redlining/fee-schedule-redline/fee-schedule-redline";
import FieldRedline, { FieldRedlineArray } from "./redlining/field-redlines/field-redline";
import ProposalRedline, { RedlinedDates, RedlinedDocuments } from "./redlining/proposal-redline";

export default class Proposal {
  public static getDefaultSpec(creator: EntityMember): ProposalParams {
    return {
      workAgreement: {
        discount: new Percent(0.03),
      },
      clientReviewers: [],
      vendorReviewers: [],
      negotiable: new AHBoolean(true),
      creator,
    };
  }

  public readonly originalProposal: Proposal;
  public readonly RFPId?: Guid;
  public readonly id?: Guid;
  public readonly creator: EntityMember;
  public readonly createdDate?: AHDate;
  public readonly lastUpdated?: AHDate;
  public readonly workAgreement: WorkAgreement;
  public readonly negotiable: AHBoolean = new AHBoolean(true);
  public readonly responseDueBy?: AHDate;

  public readonly supersededById?: Guid;
  public readonly supersedes?: ProposalParams;
  public readonly status: ProposalStatus = ProposalStatus.AwaitingSubmission;
  public readonly spec: ProposalParams;
  public readonly availableActions: { [key in ProposalAction]: Guid[] };
  public readonly partyRevision?: ProposalParams;
  public readonly userRevision?: ProposalParams;
  public readonly redlining?: ProposalRedline;
  public unresolvedFields = new Set<(typeof ProposalFieldName)[keyof typeof ProposalFieldName]>();

  public get baseSpec(): BaseProposalParams {
    return {
      creator: this.spec.creator,
      workAgreement: this.spec.workAgreement,
      clientReviewers: this.spec.clientReviewers,
      vendorReviewers: this.spec.vendorReviewers,
      negotiable: this.spec.negotiable,
    };
  }

  public get workAgreementId(): Guid | undefined {
    return this.workAgreement.id;
  }

  public get name(): ProjectName | undefined {
    return this.spec?.workAgreement?.name ?? undefined;
  }

  public get description(): ProjectDescription | undefined {
    return this.spec?.workAgreement?.description ?? undefined;
  }

  public get clientTeam(): ClientTeam | MaskedTeam | null {
    return this.workAgreement.clientTeam ?? null;
  }
  public get clientReviewersCacheKey(): string {
    const reviewerKeys: string[] = [];
    for (const reviewer of this._clientReviewers) {
      reviewerKeys.push(`${reviewer.userId.value}-${reviewer.canEdit}`);
    }
    return reviewerKeys.toSorted((a, b) => a.localeCompare(b)).join(",");
  }

  public get clientLeader(): EntityMember | MaskedTeam | null {
    if (Masked.isMasked(this.workAgreement.clientTeam)) {
      return this.workAgreement.clientTeam;
    }
    return this.workAgreement.clientTeam?.leader ?? null;
  }

  public get vendorTeam(): VendorTeam | MaskedTeam | null {
    return this.workAgreement.vendorTeam ?? null;
  }

  public get vendorReviewersCacheKey(): string {
    const reviewerKeys: string[] = [];
    for (const reviewer of this._vendorReviewers) {
      reviewerKeys.push(`${reviewer.userId.value}-${reviewer.canEdit}`);
    }
    return reviewerKeys.toSorted((a, b) => a.localeCompare(b)).join(",");
  }

  public get teamRestricted(): AHBoolean | MaskedTeam {
    return this.workAgreement.teamRestricted ?? new AHBoolean(false);
  }
  public get vendorLeader(): EntityMember | null | MaskedTeam {
    return this.workAgreement.vendor?.clone() ?? null;
  }

  public get startDate(): AHDate | undefined {
    return this.workAgreement.startDate;
  }
  public get endDate(): AHDate | undefined {
    return this.workAgreement.endDate;
  }

  public get feeSchedule(): FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule {
    if (Masked.isMasked(this.workAgreement.feeSchedule)) {
      return this.workAgreement.feeSchedule;
    }
    return [...this.workAgreement.feeSchedule];
  }

  public get clientPolicies(): WorkDocument[] | MaskedContracts {
    if (Masked.isMasked(this.workAgreement.clientPolicies)) {
      return this.workAgreement.clientPolicies;
    }
    return this.workAgreement.clientPolicies.map((doc) => doc.clone());
  }
  public get vendorPolicies(): WorkDocument[] | MaskedContracts {
    if (Masked.isMasked(this.workAgreement.vendorPolicies)) {
      return this.workAgreement.vendorPolicies;
    }
    return this.workAgreement.vendorPolicies.map((doc) => doc.clone());
  }
  public get conflicts(): WorkDocument[] | MaskedContracts {
    if (Masked.isMasked(this.workAgreement.conflicts)) {
      return this.workAgreement.conflicts;
    }
    return this.workAgreement.conflicts.map((doc) => doc.clone());
  }
  public get conflictsCheckWaived(): AHBoolean | MaskedContracts {
    return this.workAgreement.conflictsCheckWaived ?? new AHBoolean(false);
  }

  public get discount(): Percent | MaskedDiscount {
    return this.workAgreement?.discount ?? new Percent(0);
  }

  public get additionalVendors(): EntityMember[] | null {
    return this.workAgreement.additionalVendors.map((vendor) => vendor.clone()) ?? null;
  }

  public get clientTeamTemplateIds(): Guid[] {
    return this.workAgreement.clientTeamTemplateIds;
  }
  public get vendorTeamTemplateIds(): Guid[] {
    return this.workAgreement.vendorTeamTemplateIds;
  }
  public get clientFeeScheduleTemplateIds(): Guid[] {
    return this.workAgreement.clientFeeScheduleTemplateIds;
  }
  public get vendorFeeScheduleTemplateIds(): Guid[] {
    return this.workAgreement.vendorFeeScheduleTemplateIds;
  }

  public get archiveDate(): AHDate | undefined {
    return this.workAgreement.archiveDate;
  }

  public get isHired(): boolean {
    return this.workAgreement.hireDate !== undefined;
  }

  public get hireDate(): AHDate | undefined {
    return this.workAgreement.hireDate;
  }

  public get clientReviewers(): ProposalReviewer[] {
    return [...this._clientReviewers];
  }
  public get vendorReviewers(): ProposalReviewer[] {
    return [...this._vendorReviewers];
  }

  public get associatedUserIds(): Guid[] {
    const userIds: Guid[] = [...this.workAgreement.associatedUserIds];
    if (this.creator) {
      userIds.push(this.creator.userId);
    }
    this._clientReviewers.forEach((reviewer) => {
      userIds.push(reviewer.userId);
    });
    this._vendorReviewers.forEach((reviewer) => {
      userIds.push(reviewer.userId);
    });
    return userIds;
  }

  public get isModifiedByRedline(): boolean {
    return Boolean(this.redlinedRevision && !this.redlinedRevision.isEquivalentTo(this));
  }

  constructor(params: {
    currentUser: User;
    spec: ProposalParams;
    options?: {
      withRedline?: boolean;
    };
    originalProposal?: Proposal;
  }) {
    const { currentUser, spec, originalProposal } = params;
    const withRedline = params.options?.withRedline ?? false;

    this.RFPId = spec.workAgreement.RFPId ?? undefined;
    this.id = spec.id;
    this.creator = spec.creator;
    this.createdDate = spec.createdDate;
    this.lastUpdated = spec.lastUpdated;
    this.negotiable = spec.negotiable ?? new AHBoolean(true);
    this.responseDueBy = spec.responseDueBy;

    this.workAgreement = new WorkAgreement(spec.workAgreement);
    this.spec = spec;

    const workAgreementSchema = new WorkAgreementSchema();
    this.proposalSchema = new ProposalSchema(workAgreementSchema, currentUser);

    this.currentUser = currentUser;
    this._clientReviewers = spec.clientReviewers.map((reviewerParams) => new ProposalReviewer(reviewerParams)) ?? [];
    this._vendorReviewers = spec.vendorReviewers.map((reviewerParams) => new ProposalReviewer(reviewerParams)) ?? [];
    this.supersededById = spec?.supersededById;
    this.status = spec?.status ?? ProposalStatus.AwaitingSubmission;
    this.availableActions = spec.availableActions
      ? {
        [ProposalAction.Submit]: [...spec.availableActions[ProposalAction.Submit]],
        [ProposalAction.Approve]: [...spec.availableActions[ProposalAction.Approve]],
        [ProposalAction.Reject]: [...spec.availableActions[ProposalAction.Reject]],
        [ProposalAction.Revise]: [...spec.availableActions[ProposalAction.Revise]],
        [ProposalAction.Hire]: [...spec.availableActions[ProposalAction.Hire]],
        [ProposalAction.Delete]: [...spec.availableActions[ProposalAction.Delete]],
        [ProposalAction.Cancel]: [...spec.availableActions[ProposalAction.Cancel]],
        [ProposalAction.Edit]: [...spec.availableActions[ProposalAction.Edit]],
        [ProposalAction.Review]: [...spec.availableActions[ProposalAction.Review]],
      }
      : {
        [ProposalAction.Submit]: [],
        [ProposalAction.Approve]: [],
        [ProposalAction.Reject]: [],
        [ProposalAction.Revise]: [],
        [ProposalAction.Hire]: [],
        [ProposalAction.Delete]: [],
        [ProposalAction.Cancel]: [],
        [ProposalAction.Edit]: [],
        [ProposalAction.Review]: [],
      };
    this._supersededById = spec?.supersededById;

    this.supersedes = spec?.supersedes;
    this.partyRevision = spec?.partyRevision;
    this.userRevision = spec?.userRevision;

    if (spec.existingRedline) {
      this.redlining = spec.existingRedline;
    } else if (withRedline && this.isSubmitted) {
      this.redlining = this.createRedline();
      this.updateLastSavedRedline();
    }
    this.originalProposal = originalProposal ?? this;
  }

  public get isSubmitted(): boolean {
    return this.status !== ProposalStatus.AwaitingSubmission;
  }

  public get isApprovedByBothParties(): boolean {
    return [
      ProposalStatus.AwaitingApprovalByTeam,
      ProposalStatus.AwaitingHire,
      ProposalStatus.AwaitingApprovalByVendors,
    ].includes(this.status);
  }

  public get reviewersKey(): string {
    const reviewerKeys: string[] = [];
    for (const reviewer of this._clientReviewers) {
      reviewerKeys.push(`client-${reviewer.userId.value}-${reviewer.canEdit}`);
    }

    for (const reviewer of this._vendorReviewers) {
      reviewerKeys.push(`vendor-${reviewer.userId.value}-${reviewer.canEdit}`);
    }
    return reviewerKeys.toSorted((a, b) => a.localeCompare(b)).join(",");
  }

  public get areReviewersModified(): boolean {
    return (
      !_.isEqual(this._clientReviewers, this.originalProposal.clientReviewers) ||
      !_.isEqual(this._vendorReviewers, this.originalProposal.vendorReviewers)
    );
  }

  public get isArchived(): boolean {
    return this.status === ProposalStatus.Archived;
  }

  public get redline(): ProposalRedline | undefined {
    return this.redlining ?? undefined;
  }

  public get userIsLatestSubmittingParty(): boolean {
    if (Masked.isMasked(this.clientLeader)) return false;
    if (Masked.isMasked(this.vendorTeam)) return false;
    // No proposal fields are maskable by submitters

    if (this.creator?.userId.isEqualTo(this.currentUser.id)) return true;

    const creatorIsClient = this.clientLeader?.userId.isEqualTo(this.creator?.userId);
    if (creatorIsClient) {
      return this._clientReviewers.some((reviewer) => reviewer.userId.isEqualTo(this.currentUser.id));
    }

    if (Masked.isMasked(this.vendorTeam)) return false;
    const creatorIsTeamLeader = this.vendorTeam?.leader?.userId.isEqualTo(this.creator?.userId);
    if (creatorIsTeamLeader) {
      return this._vendorReviewers.some((reviewer) => reviewer.userId.isEqualTo(this.currentUser.id));
    }
    return false;
  }

  public get isModified(): boolean {
    if (this.hasUnsavedRedlineChanges || this.hasUnsharedRedlineChanges) {
      return true;
    }
    let reviewersModified = false;
    if (
      Masked.isNotMasked(this.clientLeader) &&
      this.clientLeader &&
      this.currentUser.id?.isEqualTo(this.clientLeader.userId)
    ) {
      reviewersModified =
        this._clientReviewers.length !== this.originalProposal.clientReviewers.length ||
        this._clientReviewers.some(
          (latestReviewer) =>
            !this.originalProposal.clientReviewers.some((originalReviewer) =>
              originalReviewer.isEqualTo(latestReviewer),
            ),
        );
    } else if (
      this.vendorTeam &&
      Masked.isNotMasked(this.vendorTeam) &&
      this.currentUser.id?.isEqualTo(this.vendorTeam.leader?.userId)
    ) {
      reviewersModified =
        this._vendorReviewers.length !== this.originalProposal.vendorReviewers.length ||
        this._vendorReviewers.some(
          (latestReviewer) =>
            !this.originalProposal.vendorReviewers.some((originalReviewer) =>
              originalReviewer.isEqualTo(latestReviewer),
            ),
        );
    }
    return !this.isEquivalentTo(this.originalProposal) || reviewersModified;
  }

  public get redlinedRevision(): Proposal | null {
    if (!this.redlining) return null;
    if (!this.vendorLeader) {
      throw new Error("Cannot create a redlined revision without a vendor team.");
    }
    let currentVendorTeam: VendorTeam | MaskedTeam;
    let currentTeamRestricted: AHBoolean | MaskedTeam;
    const currentTeamMembers = this.redlining.currentTeamMembers;
    if (
      Masked.isMasked(this.vendorLeader) ||
      Masked.isMasked(currentTeamMembers) ||
      Masked.isMasked(this.redlining.teamRestricted)
    ) {
      currentVendorTeam = this.vendorTeam as MaskedTeam;
      currentTeamRestricted = this.teamRestricted;
    } else {
      currentVendorTeam = new VendorTeam({
        leader: this.vendorLeader,
        members: currentTeamMembers,
      });
      currentTeamRestricted = this.redlining.teamRestricted.currentEntry ?? new AHBoolean(false);
    }
    const revisionSpec: ProposalParams = {
      ...this.spec,
      workAgreement: {
        ...this.redlining.currentProjectDates,
        ...this.redlining.currentDocuments,
        name: this.redlining.name.currentEntry ?? undefined,
        description: this.redlining.description.currentEntry ?? undefined,
        feeSchedule: this.redlining.currentFeeSchedule,
        vendorTeam: currentVendorTeam,
        clientTeam: this.clientTeam ?? undefined,
        teamRestricted: currentTeamRestricted,
        discount: this.redlining.currentDiscount,
      },
      responseDueBy: this.redlining.responseDueBy.currentEntry ?? undefined,
    };

    const revision = new Proposal({
      currentUser: this.currentUser,
      spec: {
        ...revisionSpec,
        clientReviewers: this._clientReviewers,
        vendorReviewers: this._vendorReviewers,
        workAgreement: {
          ...revisionSpec.workAgreement,
          clientTeamTemplateIds: this.clientTeamTemplateIds,
          vendorTeamTemplateIds: this.vendorTeamTemplateIds,
          clientFeeScheduleTemplateIds: this.clientFeeScheduleTemplateIds,
          vendorFeeScheduleTemplateIds: this.vendorFeeScheduleTemplateIds,
        },
      },
      originalProposal: this.originalProposal,
      options: {
        withRedline: false,
      },
    });
    return revision;
  }

  public clone(overrides?: Partial<ProposalParams | WorkAgreementParams>): Proposal {
    const clonedProposal = new Proposal({
      spec: {
        clientReviewers: this._clientReviewers.map((r) => r.clone()),
        vendorReviewers: this._vendorReviewers.map((r) => r.clone()),
        negotiable: this.negotiable.clone(),
        workAgreement: {
          additionalVendors: this.workAgreement.additionalVendors.map((vendor) => vendor.clone()),
          archiveDate: this.workAgreement.archiveDate?.clone(),
          clientFeeScheduleTemplateIds: this.workAgreement.clientFeeScheduleTemplateIds.map((id) => id.clone()),
          clientPolicies: Masked.isMasked(this.workAgreement.clientPolicies)
            ? this.workAgreement.clientPolicies
            : this.workAgreement.clientPolicies.map((doc) => doc.clone()),
          clientTeamTemplateIds: this.workAgreement.clientTeamTemplateIds.map((id) => id.clone()),
          conflictsCheckWaived: this.workAgreement.conflictsCheckWaived?.clone(),
          conflicts: Masked.isMasked(this.workAgreement.conflicts)
            ? this.workAgreement.conflicts
            : this.workAgreement.conflicts.map((doc) => doc.clone()),
          description: this.workAgreement.description?.clone(),
          discount: this.workAgreement.discount?.clone(),
          endDate: this.workAgreement.endDate?.clone(),
          feeSchedule: Masked.isMasked(this.workAgreement.feeSchedule)
            ? this.workAgreement.feeSchedule
            : this.workAgreement.feeSchedule.map((cat) => (cat as FeeScheduleCategory).clone()),
          clientTeam: Masked.isMasked(this.workAgreement.clientTeam)
            ? this.workAgreement.clientTeam
            : this.workAgreement.clientTeam?.clone(),
          vendorTeam: Masked.isMasked(this.workAgreement.vendorTeam)
            ? this.workAgreement.vendorTeam
            : this.workAgreement.vendorTeam?.clone(),
          hireDate: this.workAgreement.hireDate?.clone(),
          id: this.workAgreement.id?.clone(),
          name: this.workAgreement.name?.clone(),
          RFPId: this.workAgreement.RFPId?.clone(),
          startDate: this.workAgreement.startDate?.clone(),
          teamRestricted: this.workAgreement.teamRestricted?.clone(),
          vendorFeeScheduleTemplateIds: this.workAgreement.vendorFeeScheduleTemplateIds.map((id) => id.clone()),
          vendorPolicies: Masked.isMasked(this.workAgreement.vendorPolicies)
            ? this.workAgreement.vendorPolicies
            : this.workAgreement.vendorPolicies.map((doc) => doc.clone()),
          vendorTeamTemplateIds: this.workAgreement.vendorTeamTemplateIds.map((id) => id.clone()),
          ...overrides,
        },
        availableActions: this.availableActions,
        createdDate: this.createdDate?.clone(),
        creator: this.creator?.clone(),
        id: this.id?.clone(),
        lastUpdated: this.lastUpdated?.clone(),
        partyRevision: this.partyRevision,
        userRevision: this.userRevision,
        supersededById: this.supersededById?.clone(),
        responseDueBy: this.responseDueBy?.clone(),
        status: this.status,
        supersedes: this.supersedes,
        ...overrides,
      },
      originalProposal: this.originalProposal ?? this,
      currentUser: this.currentUser,
      options: {
        withRedline: false,
      },
    });

    clonedProposal._lastSavedRedline = this._lastSavedRedline;
    clonedProposal.unresolvedFields = new Set(this.unresolvedFields);

    return clonedProposal;
  }

  public hasFeeCategory(category: FeeScheduleCategory): boolean {
    if (Masked.isMasked(this.workAgreement.feeSchedule)) return false;
    return this.workAgreement.feeSchedule?.some((cat) => cat.name?.isEqualTo(category.name)) ?? false;
  }

  public isAssociatedWithUserId(id: Guid): boolean {
    if (this.creator?.userId.isEqualTo(id)) return true;
    if (this._clientReviewers.some((reviewer) => reviewer.userId.isEqualTo(id))) return true;
    if (this._vendorReviewers.some((reviewer) => reviewer.userId.isEqualTo(id))) return true;
    if (this.status !== ProposalStatus.AwaitingSubmission) {
      if (
        Masked.isPresentNotMasked(this.workAgreement.clientTeam) &&
        this.workAgreement.clientTeam.leader?.userId.isEqualTo(id)
      )
        return true;
      if (Masked.isMasked(this.workAgreement.vendorTeam)) return false;
      if (this.workAgreement.vendorTeam?.leader?.userId.isEqualTo(id)) return true;
      if (this.workAgreement.vendorTeam?.members.some((member) => member.userId.isEqualTo(id))) return true;
    }
    return false;
  }

  public getReviewer(userId?: Guid): ProposalReviewer | undefined {
    if (!userId) return undefined;
    return (
      this._clientReviewers.find((reviewer) => reviewer.userId.isEqualTo(userId)) ||
      this._vendorReviewers.find((reviewer) => reviewer.userId.isEqualTo(userId))
    );
  }

  public isModifiedByBuilder(builder?: ProposalBuilder | null): boolean {
    if (!builder?.currentSpec) return false;

    const currentSpec = builder.currentSpec;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checkEquality = (a: any, b: any) => (!a && b) || (a && !a.isEqualTo?.(b));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checkArrayEquality = (a: any[], b: any[]) =>
      a.length !== b.length || !a.every((item) => b.some((bItem) => bItem.isEqualTo?.(item)));

    const fieldsToCheck = [
      [currentSpec.workAgreement.teamRestricted, this.teamRestricted],
      [currentSpec.workAgreement.vendorTeam, this.vendorTeam],
      [currentSpec.workAgreement.name, this.name],
      [currentSpec.workAgreement.description, this.description],
      [currentSpec.responseDueBy, this.responseDueBy],
      [currentSpec.workAgreement.startDate, this.startDate],
      [currentSpec.workAgreement.endDate, this.endDate],
      [currentSpec.workAgreement.conflictsCheckWaived, this.conflictsCheckWaived],
      [currentSpec.workAgreement.discount, this.discount],
    ];

    for (const [specField, thisField] of fieldsToCheck) {
      if (checkEquality(specField, thisField)) return true;
    }

    if (
      Masked.isPresentNotMasked(currentSpec.workAgreement.feeSchedule) &&
      Masked.isPresentNotMasked(this.workAgreement.feeSchedule) &&
      (currentSpec.workAgreement.feeSchedule.length !== this.workAgreement.feeSchedule.length ||
        !currentSpec.workAgreement.feeSchedule.every(
          (currentCategory) =>
            Array.isArray(this.feeSchedule) && this.feeSchedule.some((category) => category.isEqualTo(currentCategory)),
        ))
    ) {
      return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const arrayChecks: [any[], any[]][] = [
      [currentSpec.workAgreement.clientTeamTemplateIds ?? [], this.clientTeamTemplateIds],
      [currentSpec.workAgreement.vendorTeamTemplateIds ?? [], this.vendorTeamTemplateIds],
      [currentSpec.workAgreement.clientFeeScheduleTemplateIds ?? [], this.clientFeeScheduleTemplateIds],
      [currentSpec.workAgreement.vendorFeeScheduleTemplateIds ?? [], this.vendorFeeScheduleTemplateIds],
      [currentSpec.clientReviewers ?? [], this._clientReviewers],
      [currentSpec.vendorReviewers ?? [], this._vendorReviewers],
    ];

    if (Masked.isNotMasked(currentSpec.workAgreement.clientPolicies) && Masked.isNotMasked(this.clientPolicies)) {
      arrayChecks.push([currentSpec.workAgreement.clientPolicies ?? [], this.clientPolicies]);
    }

    if (Masked.isNotMasked(currentSpec.workAgreement.vendorPolicies) && Masked.isNotMasked(this.vendorPolicies)) {
      arrayChecks.push([currentSpec.workAgreement.vendorPolicies ?? [], this.vendorPolicies]);
    }

    if (Masked.isNotMasked(currentSpec.workAgreement.conflicts) && Masked.isNotMasked(this.conflicts)) {
      arrayChecks.push([currentSpec.workAgreement.conflicts ?? [], this.conflicts]);
    }

    for (const [specArray, thisArray] of arrayChecks) {
      if (checkArrayEquality(specArray, thisArray)) return true;
    }

    return false;
  }

  public userCanShare(user?: User | null): boolean {
    if (!user?.id) return false;
    if (this.userCanEdit(user)) return true;
    if (this.userCanRedline(user)) return true;
    if (
      this.clientReviewers
        .concat(this.vendorReviewers)
        .some((reviewer) => reviewer.userId.isEqualTo(user.id) && reviewer.canEdit)
    )
      return true;
    return false;
  }

  public userCanEdit(user?: User | null): boolean {
    if (!user?.id) return false;
    if (!this.id) return true;

    return this.availableActions[ProposalAction.Edit].some((id) => id.isEqualTo(user.id));
  }

  public userCanRevise(user?: User | null): boolean {
    if (!user?.id) return false;

    return this.availableActions[ProposalAction.Revise].some((id) => id.isEqualTo(user.id));
  }

  public userCanRedline(user?: User | null): boolean {
    if (!user?.id) return false;
    if (this.userCanRevise(user)) return true;

    return (
      this._clientReviewers.some((r) => r.canEdit && r.userId.isEqualTo(user.id)) ||
      this._vendorReviewers.some((r) => r.canEdit && r.userId.isEqualTo(user.id))
    );
  }

  public userCanApprove(user?: User | null): boolean {
    if (!user?.id) return false;
    if (
      Masked.isMasked(this.workAgreement.conflictsCheckWaived) ||
      Masked.isMasked(this.workAgreement.conflicts) ||
      (!this.workAgreement.conflictsCheckWaived.value && this.workAgreement.conflicts.length === 0)
    ) {
      return false;
    }

    return this.availableActions[ProposalAction.Approve].some((id) => id.isEqualTo(user.id));
  }

  public userCanReject(user?: User | null): boolean {
    if (!user?.id) return false;
    return this.availableActions[ProposalAction.Reject].some((id) => id.isEqualTo(user.id));
  }

  public userIsClient(user?: User | null): boolean {
    if (!user?.id) return false;
    if (Masked.isMasked(this.clientLeader) || Masked.isMasked(this.vendorLeader)) return false;
    return this.clientLeader?.userId.isEqualTo(user.id) || false;
  }

  public userIsAssociatedWithClient(user?: User | null): boolean {
    if (!user?.id) return false;
    if (this.userIsClient(user)) return true;
    if (this.clientReviewers.some((reviewer) => reviewer.userId.isEqualTo(user.id))) return true;
    return false;
  }

  public userIsVendor(user?: User | null): boolean {
    if (!user?.id) return false;
    if (Masked.isMasked(this.vendorLeader) || Masked.isMasked(this.clientLeader)) return false;
    return this.vendorLeader?.userId.isEqualTo(user.id) || false;
  }

  public userIsAssociatedWithVendor(user?: User | null): boolean {
    if (!user?.id) return false;
    if (this.userIsVendor(user)) return true;
    if (this.vendorReviewers.some((reviewer) => reviewer.userId.isEqualTo(user.id))) return true;
    return false;
  }

  public userCanHire(user?: User | null): boolean {
    if (!user?.id) return false;
    return this.availableActions[ProposalAction.Hire].some((id) => id.isEqualTo(user.id));
  }

  public toJSON(): ProposalJSON {
    if (!this.id) {
      throw new Error("Cannot serialize a proposal without an id.");
    }
    if (!this.creator) {
      throw new Error("Cannot serialize a proposal without a creator.");
    }
    if (!this.workAgreement) {
      throw new Error("Cannot serialize a proposal without a work agreement.");
    }
    return this.proposalSchema.serialize(this.spec);
  }

  public static fromJSON(params: {
    json?: unknown;
    currentUser: User;
    options?: {
      withRedline?: boolean;
    };
    existingRedline?: ProposalRedline;
  }): Proposal | null | undefined {
    const { json } = params;
    if (isNullOrUndefined(json)) return json;
    const proposalSchema = new ProposalSchema(new WorkAgreementSchema(), params.currentUser);

    return new Proposal({
      ...params,
      spec: proposalSchema.deserialize(json),
    });
  }

  public updateRedline(redline: ProposalRedline): Proposal {
    return new Proposal({
      currentUser: this.currentUser,
      spec: {
        ...this.spec,
        existingRedline: redline,
      },
      originalProposal: this.originalProposal,
    });
  }

  public updateClientTeamTemplateIds(ids: Guid[]): Proposal {
    const newProposal = this.clone({ clientTeamTemplateIds: ids });
    return newProposal;
  }

  public updateVendorTeamTemplateIds(ids: Guid[]): Proposal {
    const newProposal = this.clone({ vendorTeamTemplateIds: ids });
    return newProposal;
  }

  public updateClientFeeScheduleTemplateIds(ids: Guid[]): Proposal {
    const newProposal = this.clone({ clientFeeScheduleTemplateIds: ids });
    return newProposal;
  }

  public updateVendorFeeScheduleTemplateIds(ids: Guid[]): Proposal {
    const newProposal = this.clone({ vendorFeeScheduleTemplateIds: ids });
    return newProposal;
  }

  public updateClientReviewers(reviewers: ProposalReviewer[]): Proposal {
    return this.clone({
      clientReviewers: reviewers,
    });
  }

  public updateVendorReviewers(reviewers: ProposalReviewer[]): Proposal {
    return this.clone({
      vendorReviewers: reviewers,
    });
  }

  public getCommentAudienceAndSubscribers(
    isExternal: boolean,
    session: Session,
  ): {
    audience: Audience;
    subscribers: EntityMember[];
  } {
    if (!session.user) throw new Error("Session user is required to get comment audience and subscribers.");
    if (!this.creator) throw new Error("Proposal creator is required to get comment audience.");

    let audience: Audience;
    const subscribers: EntityMember[] = [];

    subscribers.push(session.user.toEntityMember());

    if (isExternal) {
      audience = Audience.AllReviewers;
      subscribers.push(this.creator);

      if (Masked.isPresentNotMasked(this.clientLeader)) subscribers.push(this.clientLeader);

      if (Masked.isPresentNotMasked(this.vendorLeader)) subscribers.push(this.vendorLeader);

      subscribers.push(
        ...this._clientReviewers.map((reviewer) => reviewer.entityMember),
        ...this._vendorReviewers.map((reviewer) => reviewer.entityMember),
      );
    } else if (session.context?.viewingAsVendor) {
      audience = Audience.VendorReviewers;
      if (Masked.isPresentNotMasked(this.vendorLeader)) subscribers.push(this.vendorLeader);

      subscribers.push(...this._vendorReviewers.map((reviewer) => reviewer.entityMember));
    } else {
      audience = Audience.ClientReviewers;
      if (Masked.isPresentNotMasked(this.clientLeader)) subscribers.push(this.clientLeader);
      subscribers.push(...this._clientReviewers.map((reviewer) => reviewer.entityMember));
    }

    return {
      audience,
      subscribers: _.uniqBy(subscribers, (subscriber) => subscriber.userId.value),
    };
  }

  public isEquivalentTo(other?: Proposal): boolean {
    if (!other) return false;
    if ((this.vendorTeam && !this.vendorTeam?.isEqualTo(other.vendorTeam)) || (!this.vendorTeam && other.vendorTeam))
      return false;
    if (!this.hasEquivalentContracts(other)) return false;
    if (!this.discount.isEqualTo(other.discount)) return false;
    if (!this.teamRestricted.isEqualTo(other.teamRestricted)) return false;

    if (
      Masked.isNotMasked(this.workAgreement.feeSchedule) &&
      Masked.isNotMasked(other.feeSchedule) &&
      (this.workAgreement.feeSchedule?.length !== other.feeSchedule.length ||
        !this.workAgreement.feeSchedule?.every(
          (category, index) => Masked.isNotMasked(other.feeSchedule) && category.isEqualTo(other.feeSchedule[index]),
        ))
    ) {
      return false;
    }

    const namesEqual = (!this.name && !other.name) || this.name?.isEqualTo(other.name);

    const descriptionsEqual =
      (!this.description && !other.description) ||
      (this.description && other.description && this.description.isEqualTo(other.description));

    const clientTeamsEmpty =
      !Masked.isPresentNotMasked(this.workAgreement.clientTeam) &&
      !Masked.isPresentNotMasked(other.workAgreement.clientTeam);

    const clientTeamsEqual = this.workAgreement.clientTeam?.isEqualTo(other.workAgreement.clientTeam);

    const responseDueByEqual =
      (!this.responseDueBy && !other.responseDueBy) || this.responseDueBy?.isEqualTo(other.responseDueBy);

    const startDatesEqual = (!this.startDate && !other.startDate) || this.startDate?.isEqualTo(other.startDate);

    const endDatesEqual = (!this.endDate && !other.endDate) || this.endDate?.isEqualTo(other.endDate);

    return Boolean(
      (clientTeamsEmpty || clientTeamsEqual) &&
      namesEqual &&
      descriptionsEqual &&
      this.negotiable.isEqualTo(other.negotiable) &&
      responseDueByEqual &&
      startDatesEqual &&
      endDatesEqual,
    );
  }

  public createRedline(): ProposalRedline | undefined {
    if (!this.id || !this.name || !this.description || !this.workAgreement) return;

    let originalVersion: Proposal = this.clone();
    let revisedVersion: Proposal = originalVersion;
    if (this.supersedes && !this.isApprovedByBothParties) {
      originalVersion = new Proposal({
        currentUser: this.currentUser,
        spec: this.supersedes,
        options: {
          withRedline: false,
        },
      });
      revisedVersion = this.userIsLatestSubmittingParty ? originalVersion : this.clone();
    }

    let currentVersion: Proposal | undefined = this.userIsLatestSubmittingParty ? revisedVersion : undefined;
    if (this.partyRevision?.createdDate?.isAfter(this.supersedes?.createdDate)) {
      currentVersion = new Proposal({
        currentUser: this.currentUser,
        spec: {
          ...this.partyRevision,
        },
        options: {
          withRedline: false,
        },
      });
    }
    if (this.userRevision && (!currentVersion || this.userRevision.lastUpdated?.isAfter(currentVersion.lastUpdated))) {
      currentVersion = new Proposal({
        currentUser: this.currentUser,
        spec: {
          ...this.userRevision,
        },
        options: {
          withRedline: false,
        },
      });
    }

    const nameRedline = new FieldRedline<ProjectName>({
      field: ProposalField.Name,
      originalEntry: originalVersion.name ?? null,
      revisedEntry: revisedVersion.name ?? null,
      currentEntry: currentVersion?.unresolvedFields.has(ProposalFieldName.Name) ? undefined : currentVersion?.name,
      diffWords: true,
    });

    const descriptionRedline = new FieldRedline<ProjectDescription>({
      field: ProposalField.Description,
      originalEntry: originalVersion.description ?? null,
      revisedEntry: revisedVersion.description ?? null,
      currentEntry: currentVersion?.unresolvedFields.has(ProposalFieldName.Description)
        ? undefined
        : currentVersion?.description,
      diffWords: true,
    });

    const feeScheduleRedline =
      Masked.isMasked(originalVersion?.feeSchedule) ||
        Masked.isMasked(revisedVersion.feeSchedule) ||
        Masked.isMasked(currentVersion?.feeSchedule)
        ? new MaskedFeeSchedule()
        : new FeeScheduleRedline(originalVersion.feeSchedule, revisedVersion.feeSchedule, currentVersion?.feeSchedule);

    const teamRestrictedRedline =
      Masked.isMasked(originalVersion.teamRestricted) ||
        Masked.isMasked(this?.teamRestricted) ||
        Masked.isMasked(currentVersion?.teamRestricted)
        ? new MaskedTeam()
        : new FieldRedline<AHBoolean>({
          field: ProposalField.TeamRestriction,
          originalEntry: originalVersion.teamRestricted ?? null,
          revisedEntry: revisedVersion.teamRestricted ?? null,
          currentEntry: currentVersion?.teamRestricted,
          diffWords: false,
        });

    const conflictsCheckWaivedRedline =
      Masked.isMasked(originalVersion.conflictsCheckWaived) ||
        Masked.isMasked(this.conflictsCheckWaived) ||
        Masked.isMasked(currentVersion?.conflictsCheckWaived)
        ? new MaskedContracts()
        : new FieldRedline<AHBoolean>({
          field: ProposalField.WaiveConflictsCheck,
          originalEntry: originalVersion.conflictsCheckWaived,
          revisedEntry: revisedVersion.conflictsCheckWaived,
          currentEntry: currentVersion?.conflictsCheckWaived,
          diffWords: false,
        });

    const waivingConflictsChecks = Boolean(
      Masked.isNotMasked(conflictsCheckWaivedRedline) &&
      conflictsCheckWaivedRedline.isAccepted &&
      conflictsCheckWaivedRedline.currentEntry?.isEqualTo(new AHBoolean(true)),
    );

    const discountRedline =
      Masked.isMasked(originalVersion.discount) ||
        Masked.isMasked(this.discount) ||
        Masked.isMasked(currentVersion?.discount)
        ? (originalVersion.discount as MaskedDiscount)
        : new FieldRedline<Percent>({
          field: ProposalField.Discount,
          originalEntry: originalVersion.discount ?? null,
          revisedEntry: revisedVersion.discount ?? null,
          currentEntry: currentVersion?.discount,
          diffWords: false,
        });

    return new ProposalRedline({
      name: nameRedline,
      description: descriptionRedline,
      ...this.createDatesRedline({ originalVersion, revisedVersion, currentVersion }),
      vendorTeam: this.createTeamRedline({ originalVersion, revisedVersion, currentVersion }),
      feeSchedule: feeScheduleRedline,
      conflictsCheckWaived: conflictsCheckWaivedRedline,
      teamRestricted: teamRestrictedRedline,
      ...this.createDocumentsRedline({ originalVersion, revisedVersion, currentVersion, waivingConflictsChecks }),
      discount: discountRedline,
      lastUpdated: new AHDate(),
    });
  }

  public updateLastSavedRedline() {
    this._lastSavedRedline = this.redlining;
  }

  public get hasUnsharedRedlineChanges(): boolean {
    if (!this.redlining && !this.partyRevision) {
      return false;
    }

    if (!this.redlining && this.partyRevision) {
      console.warn("Party revision exists without redlining");
      return true;
    }

    const schema = new WorkAgreementSchema();
    const currentRedlineSpec = schema.serialize(this.redlinedRevision!.baseSpec.workAgreement);
    const partyRedlineSpec = this.partyRevision ? schema.serialize(this.partyRevision.workAgreement) : undefined;
    return !_.isEqual(currentRedlineSpec, partyRedlineSpec);
  }

  public get hasUnsavedRedlineChanges(): boolean {
    if (!this.redlining && !this._lastSavedRedline) {
      return false;
    }
    if (this.redlining && !this._lastSavedRedline) {
      return true;
    }
    if (!this.redlining && this._lastSavedRedline) {
      return true;
    }

    return !this.redlining?.isEqualTo(this._lastSavedRedline);
  }

  private readonly _clientReviewers: ProposalReviewer[] = [];
  private readonly _vendorReviewers: ProposalReviewer[] = [];
  private readonly proposalSchema: ProposalSchema;
  private readonly currentUser: User;
  private readonly _supersededById?: Guid = undefined;
  private _lastSavedRedline: ProposalRedline | undefined;

  private hasEquivalentContracts(other: Proposal): boolean {
    if (!this.conflictsCheckWaived.isEqualTo(other.conflictsCheckWaived)) return false;

    if (
      Masked.isNotMasked(this.workAgreement.conflicts) &&
      Masked.isNotMasked(other.conflicts) &&
      (this.workAgreement.conflicts?.length !== other.conflicts.length ||
        !this.workAgreement.conflicts?.every(
          (category, index) => Masked.isNotMasked(other.conflicts) && category.is(other.conflicts[index]),
        ))
    ) {
      return false;
    }

    if (
      Masked.isNotMasked(this.workAgreement.clientPolicies) &&
      Masked.isNotMasked(other.clientPolicies) &&
      (this.workAgreement.clientPolicies?.length !== other.clientPolicies.length ||
        !this.workAgreement.clientPolicies?.every(
          (category, index) => Masked.isNotMasked(other.clientPolicies) && category.is(other.clientPolicies[index]),
        ))
    ) {
      return false;
    }

    if (
      Masked.isNotMasked(this.workAgreement.vendorPolicies) &&
      Masked.isNotMasked(other.vendorPolicies) &&
      (this.workAgreement.vendorPolicies?.length !== other.vendorPolicies.length ||
        !this.workAgreement.vendorPolicies?.every(
          (category, index) => Masked.isNotMasked(other.vendorPolicies) && category.is(other.vendorPolicies[index]),
        ))
    ) {
      return false;
    }

    return true;
  }

  private createDatesRedline(params: {
    originalVersion: Proposal;
    revisedVersion: Proposal;
    currentVersion?: Proposal;
  }): RedlinedDates {
    const { originalVersion, revisedVersion, currentVersion } = params;
    return {
      responseDueBy: new FieldRedline({
        field: ProposalField.ResponseDueBy,
        originalEntry: originalVersion.responseDueBy ?? null,
        revisedEntry: revisedVersion.responseDueBy ?? null,
        currentEntry: currentVersion?.responseDueBy === undefined
          ? undefined
          : currentVersion?.responseDueBy ?? null,
        diffWords: false,
      }),
      startDate: new FieldRedline({
        field: ProposalField.StartDate,
        originalEntry: originalVersion.startDate ?? null,
        revisedEntry: revisedVersion.startDate ?? null,
        currentEntry: currentVersion?.startDate === undefined
          ? undefined
          : currentVersion?.startDate ?? null,
        diffWords: false,
      }),
      endDate: new FieldRedline({
        field: ProposalField.EndDate,
        originalEntry: originalVersion.endDate ?? null,
        revisedEntry: revisedVersion.endDate ?? null,
        currentEntry: currentVersion?.endDate === undefined
          ? undefined
          : currentVersion?.endDate ?? null,
        diffWords: false,
      }),
    };
  }

  private createTeamRedline(params: {
    originalVersion: Proposal;
    revisedVersion: Proposal;
    currentVersion?: Proposal;
  }): FieldRedlineArray<EntityMember> | MaskedTeam {
    const { originalVersion, revisedVersion, currentVersion } = params;
    return Masked.isMasked(originalVersion.vendorTeam) ||
      Masked.isMasked(revisedVersion.vendorTeam) ||
      Masked.isMasked(currentVersion?.vendorTeam)
      ? new MaskedTeam()
      : FieldRedlineArray.create<EntityMember>({
        field: ProposalField.Team,
        userIsLatestSubmittingParty: this.userIsLatestSubmittingParty,
        entryField: ProposalField.TeamMember,
        originalEntries:
          originalVersion.vendorTeam?.members.map((memberParams) => EntityMember.create({ ...memberParams })) ?? [],
        revisedEntries:
          revisedVersion.vendorTeam?.members.map((memberParams) => EntityMember.create({ ...memberParams })) ?? [],
        currentEntries: currentVersion?.unresolvedFields.has(ProposalFieldName.Team)
          ? []
          : (currentVersion?.vendorTeam?.members.map((memberParams) => EntityMember.create({ ...memberParams })) ??
            []),
      });
  }

  private createDocumentsRedline(params: {
    originalVersion: Proposal;
    revisedVersion: Proposal;
    currentVersion?: Proposal;
    waivingConflictsChecks: boolean;
  }): RedlinedDocuments {
    const { originalVersion, revisedVersion, currentVersion, waivingConflictsChecks } = params;
    const documentsRedline = {
      clientPolicies:
        Masked.isMasked(originalVersion?.clientPolicies) || Masked.isMasked(revisedVersion.clientPolicies) || Masked.isMasked(currentVersion?.clientPolicies)
          ? new MaskedContracts()
          : FieldRedlineArray.create<WorkDocument>({
            field: ProposalField.ClientPolicies,
            userIsLatestSubmittingParty: this.userIsLatestSubmittingParty,
            entryField: ProposalField.ClientPolicy,
            originalEntries: originalVersion.clientPolicies.map((doc) => doc.clone()) ?? [],
            revisedEntries: revisedVersion.clientPolicies.map((doc) => doc.clone()) ?? [],
            currentEntries: currentVersion?.clientPolicies.map((doc) => doc.clone()) ?? [],
          }),
      vendorPolicies:
        Masked.isMasked(originalVersion?.vendorPolicies) || Masked.isMasked(revisedVersion.vendorPolicies) || Masked.isMasked(currentVersion?.vendorPolicies)
          ? new MaskedContracts()
          : FieldRedlineArray.create<WorkDocument>({
            field: ProposalField.VendorPolicies,
            userIsLatestSubmittingParty: this.userIsLatestSubmittingParty,
            entryField: ProposalField.VendorPolicy,
            originalEntries: originalVersion.vendorPolicies.map((doc) => doc.clone()) ?? [],
            revisedEntries: revisedVersion.vendorPolicies.map((doc) => doc.clone()) ?? [],
            currentEntries: currentVersion?.vendorPolicies.map((doc) => doc.clone()) ?? [],
          }),
      conflicts:
        Masked.isMasked(originalVersion?.conflicts) || Masked.isMasked(revisedVersion.conflicts) || Masked.isMasked(currentVersion?.conflicts)
          ? new MaskedContracts()
          : FieldRedlineArray.create<WorkDocument>({
            field: ProposalField.Conflicts,
            userIsLatestSubmittingParty: this.userIsLatestSubmittingParty,
            entryField: ProposalField.ConflictsDocument,
            originalEntries: originalVersion.conflicts.map((doc) => doc.clone()) ?? [],
            revisedEntries: revisedVersion.conflicts.map((doc) => doc.clone()) ?? [],
            currentEntries: currentVersion?.conflicts.map((doc) => doc.clone()) ?? [],
          }),
    };

    if (waivingConflictsChecks && Masked.isNotMasked(documentsRedline.conflicts)) {
      documentsRedline.conflicts = documentsRedline.conflicts.acceptAll();
    }
    return documentsRedline;
  }
}
